import { TranslateService } from '@ngx-translate/core';
import { Component, Input, OnInit, Output, EventEmitter, SimpleChanges, AfterViewInit, OnChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { AuthenticationService, CredentialsService } from '@app/login';
import {
  DigitalIdDTO,
  DigitalIdResponseDTO,
  FinancingCallDTO,
  StatesFieldsFrontDTO,
} from '@app/requests/model/requests-model';
import {
  FINANCE_AMOUNT,
  FINANCIAL_PRODUCT_MG,
  FORM_CONSUMO,
  OPERATIVE_EIC,
  POINT_OF_SALE,
  REQUEST_DATA,
  REQUEST_MODE_EDIT,
  REQUEST_MODE_NEW,
  REQUEST_MODE_VIEW,
} from '@app/@core/constants/requests.constants';
import { forkJoin } from 'rxjs';
import { RequestsSaveSendStore } from '@app/requests/requests-save-send-service/requests-save-send.store';
import { CombosStore } from '@app/requests/combos-service/combos.store';
import {
  ComercialProductResponseDTO,
  CommonDataDTO,
  FinancialProduct,
  TariffDTO,
  VendorComboDTO,
} from '@app/requests/model/combos-model';
import { CustomValidatorsService } from '@app/@shared/custom-validators/custom-validators.service';
import { UserAttributionsEnum } from '@app/@core/constants/user-credentials.constants';
import { finalize } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ResponseDialogComponent } from '@app/@shared/dialogs/response/response-dialog.component';
import { RequestsGetDataApi } from '@app/requests/requests-get-data-service/requests-get-data-api.service';

@Component({
  selector: 'scf-request-data-uptake',
  templateUrl: './request-data-uptake.component.html',
  styleUrls: ['./request-data-uptake.component.scss'],
})
export class RequestDataUptakeComponent implements OnChanges, OnInit, AfterViewInit {
  @Input() public isLoadingFinance: boolean;
  @Input() public isCalculator: boolean;
  @Input('establishment') set dataEstablishment(data: any) {
    if (data) {
      this.selectedEstablishment = data;
      this._selectedProductLine = null;
      this.selectedCommercialProduct = null;
      this.requestDataUptakeForm?.controls.tariffs.setValue(null);
      this.tariffsList = [];
    }
  }
  @Input() public mode: string = 'new';
  @Input() public request: any = null;
  @Input() state: CommonDataDTO;
  @Input('expirationMonth') set dataExpirationMonth(data: { value: string }) {
    if (data?.value) {
      this.requestDataUptakeForm?.controls.expirationMonth.setValue(this.translateService.instant(data.value));
    } else {
      this.requestDataUptakeForm?.controls?.expirationMonth.setValue(null);
    }
  }
  @Input('operative') set dataOperative(operative: { value: any }) {
    if (operative?.value) {
      this._operative = operative.value;
      this.changesDetected(this._operative);
      this.maxLength = operative.value === OPERATIVE_EIC ? 13 : 20;
      if (operative.value === OPERATIVE_EIC) {
        if (this.mode === REQUEST_MODE_NEW) {
          if (this.expirationDaysList) {
            this.requestDataUptakeForm?.controls.expirationDay.setValue(this.expirationDaysList[0]?.id);
            this.requestDataUptakeForm?.controls.expirationDay.disable();
          }
        }
      }
    }
  }

  @Input('selectedProductLine') set selectedProductLine(data: any) {
    this.requestDataUptakeForm?.controls.tariffs.setValue(null);
    if (this._selectedProductLine != data && data != null) {
      this._selectedProductLine = data;
      this.getTariff();
    }
  }

  @Input() public requestedAmount: string;
  @Input() public priceProduct: string;
  @Input() public start: string;
  @Input() public isDisableSendClientButton: boolean;
  @Input() public vendor: VendorComboDTO;
  @Output() public maxAmount = new EventEmitter<any>();
  @Output() public changeContinueStatus = new EventEmitter<any>();
  @Output() public sendDataFinance = new EventEmitter<any>();
  @Output() public isFinishedLoading = new EventEmitter<any>();

  public requestDataUptakeForm: FormGroup;
  expirationDateList: CommonDataDTO[] = [];
  commercialProductResponse: ComercialProductResponseDTO[] = [];
  commercialProductList: CommonDataDTO[] = [];
  expirationDaysList: CommonDataDTO[];
  tariffsList: TariffDTO[] = [];
  formPaymentList: CommonDataDTO[];
  _operative: string;
  _selectedProductLine: any;
  monthsList: string[] = [];
  selectedEstablishment: string;
  selectedCommercialProduct: string;
  financialProduct: FinancialProduct;
  userInfo: any;
  isDisabled: boolean = true;
  isLoading: boolean;
  maxLength: number;
  isRepsol: boolean;
  isGS: boolean;
  showExpMonth: boolean;
  showPaymentMode: boolean;
  calculatorData: boolean = false;
  maxAmountByProduct: any;

  //Loading
  private isComponentLoading: boolean = true;
  private isExpirationDaysLoading: boolean = true;
  private isCombosUpdateLoading: boolean = false;
  private isChangesLoading: boolean = false;

  private isTariffLoading: boolean = false;
  private isModalityPaymentLoading: boolean = false;

  //PE_725 Fenix
  fieldsDisabled: StatesFieldsFrontDTO[];

  constructor(
    private formBuilder: FormBuilder,
    private requestStore: RequestsSaveSendStore,
    private credentials: CredentialsService,
    private combosStore: CombosStore,
    private authenticationService: AuthenticationService,
    private customValidators: CustomValidatorsService,
    private translateService: TranslateService,
    public dialog: MatDialog,
    private requestsGetDataApi: RequestsGetDataApi
  ) {
    this.userInfo = this.credentials.credentials;
    this.isRepsol = this.authenticationService.isRepsol();
    this.isGS = this.authenticationService.isGS();
  }

  ngAfterViewInit(): void {
    this.isComponentLoading = false;
    this.changeLoadingState();
    //PE_725 Fenix
    if (this.mode === REQUEST_MODE_EDIT && this.request !== null && this.request.fieldsDisable !== null) {
      this.fieldsDisabled = this.request.fieldsDisable;
      this.disableFieldsByCore();
    }
  }

  ngOnInit(): void {
    this.requestDataUptakeForm = this.formBuilder.group({
      commercialProduct: ['', Validators.required],
      term: [''],
      expirationDay: ['', Validators.required],
      expirationMonth: [{ value: '', disabled: true }],
      tariffs: ['', Validators.required],
      formPayment: ['', Validators.required],
      reference: [''],
      insurance: [''],
      share: [''],
    });

    this.monthsList = this.combosStore.getMonthsList();
    if (
      this.mode === REQUEST_MODE_VIEW &&
      this.authenticationService.permissionAction(UserAttributionsEnum.ATTR_SEARCH_OPERATION)
    ) {
      this.financialProduct = { financialProduct: +this.request.commercialProduct.substring(0, 2) };
    } else {
      if (this.isRepsol || this.isGS) {
        this.financialProduct = { financialProduct: 30 };
        this.showExpMonth = false;
      } else {
        this.financialProduct = { financialProduct: 10 };
        this.showExpMonth = true;
      }
    }
    this.combosStore
      .loadExpirationDays(10)
      .then(
        (expirationDays: CommonDataDTO[]) => {
          this.expirationDaysList = expirationDays;

          //first day by default(ESPSCFWMTS-5245)
          if (this.request?.expirationDay)
            this.requestDataUptakeForm.controls.expirationDay.setValue(this.request.expirationDay);
          else this.requestDataUptakeForm.controls.expirationDay.setValue(this.expirationDaysList[0].id);

          //setear el combo si solo tiene 1 valor
          if (this.expirationDaysList.length === 1 || (this._operative && this._operative === 'EIC')) {
            this.requestDataUptakeForm.controls.expirationDay.setValue(this.expirationDaysList[0].id);
          }

          if (this._operative && this._operative === 'EIC') {
            this.requestDataUptakeForm.controls.expirationDay.disable();
          }
        },
        (error) => {
          console.error(error);
        }
      )
      .finally(() => {
        this.isExpirationDaysLoading = false;
        this.changeLoadingState();
      });

    if (this.requestStore.getMarcaFormulario() === FORM_CONSUMO) {
      if (this.requestStore.listaFormulariosConsumo[REQUEST_DATA]?.valid) {
        this.isCombosUpdateLoading = true;
        this.updateCombos(this.requestStore.listaFormulariosConsumo[REQUEST_DATA]);
        if (this.mode === REQUEST_MODE_NEW && this.calculatorData && this.requestDataUptakeForm.valid) {
          this.calculateFinance();
          this.calculatorData = false;
        }
      } else {
        this.requestStore.setFormularioConsumo(this.requestDataUptakeForm, REQUEST_DATA);
      }
    }

    this.requestDataUptakeForm.statusChanges.subscribe(() => {
      this.isDisabled = this.requestDataUptakeForm.invalid;
      this.changeContinueStatus.emit({ form: <FormGroup>this.requestDataUptakeForm, id: REQUEST_DATA });
    });

    this.requestStore.isCalculatorData$.subscribe((data) => {
      this.calculatorData = data;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.establishment) {
      this.reset();
    }
    if (changes?.start) {
      this.changesDetected(this._operative);
    }
    this.changesDetected(null);
    if (changes.expirationMonth && !changes.expirationMonth.firstChange) {
      this.requestDataUptakeForm.controls.expirationMonth.setValue(
        this.translateService.instant(changes.expirationMonth.currentValue)
      );
    }
  }

  changesDetected(operative: string): void {
    if (this.requestStore.formReset) {
      this.selectedEstablishment = null;
      this.requestStore.setFormReset(false);
    }
    if (
      this.selectedEstablishment !== null &&
      this.selectedEstablishment !== undefined &&
      this.selectedEstablishment.length > 0
    ) {
      let amount = this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls['requestedAmount'].value
        ? this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls['requestedAmount'].value
            .replaceAll('.', '')
            .replaceAll(',', '.')
            .replaceAll('€', '')
            .trim()
        : 0;
      if (operative) {
        this.isChangesLoading = true;
        this.changeLoadingState();
        this.commercialProductList = null;
        forkJoin([this.combosStore.loadCommercialProduct(this.selectedEstablishment, operative, parseFloat(amount))])
          .pipe(
            finalize(() => {
              this.isChangesLoading = false;
              this.changeLoadingState();
            })
          )
          .subscribe((respuestaFork: any) => {
            this.commercialProductResponse = respuestaFork[0];
            this.commercialProductList = this.combosStore.ComercialProductResponseToCommonData(respuestaFork[0]);

            if (this.mode === REQUEST_MODE_EDIT && this.request) {
              this.getMaxAmountByCommercailProduct(this.request.commercialProduct);
              this.requestDataUptakeForm.controls.commercialProduct.setValue(this.request.commercialProduct);
              this.requestDataUptakeForm.controls.term.setValue(this.request.numPlaz);
              this.requestDataUptakeForm.controls.reference.setValue(this.request.reference);
              this.vendor = this.request.vendor;
            } else if (this.requestStore.getRequestDataUptake() && this.mode === REQUEST_MODE_NEW) {
              //send calculator data to request new
              let requestDataUptake = this.requestStore.getRequestDataUptake();
              this.requestDataUptakeForm.controls.commercialProduct.setValue(
                requestDataUptake.controls.commercialProduct.value
              );
            } else {
              this.maxAmountByProduct = respuestaFork[0][0].maxAmount;
              this.maxAmount.emit(respuestaFork[0][0].maxAmount);
              this.requestDataUptakeForm.controls.commercialProduct.setValue(this.commercialProductList[0].id);
            }

            this.onCommercialProductSelected();
          });
      }
    }
  }

  getTariff() {
    if (this.selectedEstablishment && this._selectedProductLine && this.selectedCommercialProduct) {
      this.isTariffLoading = true;
      this.isModalityPaymentLoading = true;
      this.changeLoadingState();
      //cargar métodos de pago
      this.combosStore
        .loadFormPayment(this.selectedCommercialProduct.substring(0, 2), this.selectedCommercialProduct) //this.financialProduct.financialProduct.toString(), this.selectedCommercialProduct)
        .then((data) => {
          this.formPaymentList = data;
          if (this.formPaymentList.length === 1) {
            this.requestDataUptakeForm.controls.formPayment.setValue(this.formPaymentList[0].id);
          } else if (this.formPaymentList.length > 0 && this.requestStore.getRequestDataUptake()) {
            let requestDataUptake = this.requestStore.getRequestDataUptake();
            this.requestDataUptakeForm.controls.formPayment.setValue(requestDataUptake.controls.formPayment.value);
          }
        })
        .finally(() => {
          this.isModalityPaymentLoading = false;
          this.changeLoadingState();
        });

      this.combosStore
        .loadComboTariffs(this.selectedEstablishment, this.selectedCommercialProduct, this._selectedProductLine)
        .then(
          (data) => {
            this.tariffsList = data;

            if (this.requestStore.getRequestDataUptake() && this.tariffsList.length > 0) {
              let requestDataUptake = this.requestStore.getRequestDataUptake();
              this.requestDataUptakeForm.controls.tariffs.setValue(requestDataUptake.controls.tariffs.value);
              this.requestDataUptakeForm.controls.expirationDay.setValue(
                requestDataUptake.controls.expirationDay.value
              );
              this.requestDataUptakeForm.controls.expirationMonth.setValue(
                requestDataUptake.controls.expirationMonth.value
              );
              this.requestDataUptakeForm.controls.reference.setValue(requestDataUptake.controls.reference.value);
              this.requestDataUptakeForm.controls.insurance.setValue(requestDataUptake.controls.insurance.value);
              this.requestDataUptakeForm.controls.share.setValue(requestDataUptake.controls.share.value);
            } else {
              //setear combos si solo tienen 1 valor
              if (this.tariffsList.length === 1) {
                this.requestDataUptakeForm.controls.tariffs.setValue(this.tariffsList[0].codigo);
              } else if (this.requestStore.getMarcaFormulario() === FORM_CONSUMO && this.mode != REQUEST_MODE_NEW) {
                this.requestDataUptakeForm.controls.tariffs.setValue(this.request.tariffSelected);
              }
            }
          },
          () => {
            this.tariffsList = null;
          }
        )
        .finally(() => {
          this.isTariffLoading = false;
          setTimeout(() => {
            this.changeLoadingState();
          }, 100);
          if (this.mode === REQUEST_MODE_VIEW) {
            this.setValuesToForm();
            this.requestDataUptakeForm.disable();
          }
        });
    }
  }

  private getVideoSelfingDigital(digitalID: DigitalIdDTO) {
    if (this.mode !== REQUEST_MODE_VIEW) {
      this.requestsGetDataApi.getlVideoSelfingDigitalID(digitalID).subscribe(
        (data: DigitalIdResponseDTO) => {
          if (data?.digitalId) {
            if (this.mode == REQUEST_MODE_NEW) {
              this.requestStore.setVideoSelfingID(data.digitalId);
            }
            this.requestStore.setImporteMaxVideoSelfing(data.impMaxOpe11);
          } else {
            this.requestStore.setVideoSelfingID('N');
            this.requestStore.setImporteMaxVideoSelfing(data.impMaxOpe11);
          }
        },
        (error) => {
          this.requestStore.setVideoSelfingID(null);
        }
      );
    }
  }

  onCommercialProductSelected(event?: MatSelect) {
    const oldCommercialProduct: string = this.selectedCommercialProduct;
    const actualCommercialProduct: string = this.requestDataUptakeForm.controls.commercialProduct.value;
    if (actualCommercialProduct) {
      if (
        oldCommercialProduct &&
        actualCommercialProduct.length === 4 &&
        oldCommercialProduct.length === 4 &&
        actualCommercialProduct.substring(0, 2) === FINANCIAL_PRODUCT_MG &&
        oldCommercialProduct?.substring(0, 2) !== FINANCIAL_PRODUCT_MG
      ) {
        const dialogConfig: MatDialogConfig = {
          width: '90%',
          maxWidth: '500px',
          data: {
            title: this.translateService.instant('requestDataUptake.dialog.product.title'),
            text: this.translateService.instant('requestDataUptake.dialog.product.body'),
            noRedirect: true,
            buttonCancel: true,
            hasButtonAccept: true,
          },
        };
        const commercialProductDialog = this.dialog.open(ResponseDialogComponent, dialogConfig);
        commercialProductDialog.afterClosed().subscribe((result) => {
          if (result) {
            this.selectedCommercialProduct = actualCommercialProduct;
            this.requestStore.setFinancialProduct(actualCommercialProduct.substring(0, 2));
          } else {
            this.requestDataUptakeForm.controls.commercialProduct.setValue(oldCommercialProduct);
          }
          setTimeout(() => {
            this.getTariff();
          }, 400);
        });
      } else {
        this.selectedCommercialProduct = actualCommercialProduct;
        this.requestStore.setFinancialProduct(actualCommercialProduct.substring(0, 2));
        setTimeout(() => {
          this.getTariff();
        }, 400);
      }
    }
    if (event) {
      if (this.commercialProductResponse.length > 1) {
        this.getMaxAmountByCommercailProduct(event.value);
      }
      this.sendDataFinance.emit({});
    }
    this.showExpMonth =
      (!this.isGS || !this.isRepsol) && this.selectedCommercialProduct?.substring(0, 2) !== FINANCIAL_PRODUCT_MG;

    if (this.requestDataUptakeForm.controls.commercialProduct.value) {
      this.getVideoSelfingDigital({
        establishment: this.requestStore.listaFormulariosConsumo[POINT_OF_SALE].controls.establishment.value?.slice(
          0,
          -1
        ),
        financialProduct: this.requestDataUptakeForm.controls.commercialProduct.value?.slice(0, 2),
      });
    }
  }

  private getMaxAmountByCommercailProduct(idCommercialProduct: any): any {
    this.commercialProductResponse.forEach((element) => {
      if (element.id === idCommercialProduct) {
        this.maxAmountByProduct = element.maxAmount;
        this.maxAmount.emit(element.maxAmount);
      }
    });
  }

  resetTariff() {
    if (this.mode === REQUEST_MODE_NEW) {
      this.requestDataUptakeForm.controls.tariffs.reset();
    }
  }

  calculateFinance(clickButton?: boolean) {
    let invalid: number = 0;
    Object.keys(this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls).forEach((key) => {
      if (
        this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls[key].value == null ||
        this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls[key].value.trim() == '' ||
        (key !== 'start' && this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls[key]?.errors !== null)
      ) {
        invalid++;
        if (
          (key === 'product' || key === 'lineProduct') &&
          this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls[key].touched
        ) {
          this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls[key].setErrors({
            showErrorCalculate: true,
          });
        }
      } else if (key === 'requestedAmount') {
        let amount = parseFloat(
          this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls.requestedAmount.value
            .toString()
            .replaceAll('.', '')
            .replace(',', '.')
        );
        let price = parseFloat(
          this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls.priceProduct.value
            .toString()
            .replaceAll('.', '')
            .replace(',', '.')
        );
        if (amount > price) {
          invalid++;
          this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls[key].setErrors({ AmountInvalid: true });
        } else if (this.maxAmountByProduct && amount > this.maxAmountByProduct) {
          invalid++;
          this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls[key].setErrors({ MaxAmount: true });
        } else {
          this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls[key].setErrors(null);
        }
      } else {
        this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls[key].setErrors(null);
      }
    });

    Object.keys(this.requestStore.listaFormulariosConsumo[POINT_OF_SALE].controls).forEach((key) => {
      if (this.requestStore.listaFormulariosConsumo[POINT_OF_SALE].controls[key].value === null) {
        invalid++;
      } else if (key !== 'requestDate') {
        this.requestStore.listaFormulariosConsumo[POINT_OF_SALE].controls[key].setErrors(null);
      }
    });

    Object.keys(this.requestStore.listaFormulariosConsumo[REQUEST_DATA].controls).forEach((key) => {
      if (
        this.requestStore.listaFormulariosConsumo[REQUEST_DATA].controls[key].value === null &&
        key !== 'insurance' &&
        key !== 'reference' &&
        key !== 'term' &&
        key !== 'expirationMonth' &&
        key !== 'share'
      ) {
        invalid++;
        this.requestStore.listaFormulariosConsumo[REQUEST_DATA].controls[key].setErrors({ required: true });
        this.requestStore.listaFormulariosConsumo[REQUEST_DATA].controls[key].setErrors({ showErrorCalculate: true });
      } else {
        this.requestStore.listaFormulariosConsumo[REQUEST_DATA].controls[key]?.setErrors(null);
      }
    });
    if (invalid === 0 && !this.isDisableSendClientButton) {
      if (this._operative === 'EIC' || this.requestDataUptakeForm.controls.term.value?.toString().length > 0) {
        let dataCallSimulation: FinancingCallDTO = {
          user: this.userInfo.username,
          establishment: this.selectedEstablishment
            ? this.selectedEstablishment
            : this.requestStore.listaFormulariosConsumo[POINT_OF_SALE].controls['establishment'].value,
          comercialProduct: this.requestDataUptakeForm.controls.commercialProduct.value,
          financialProduct: parseInt(this.selectedCommercialProduct.substring(0, 2)),
          financial:
            this.mode === REQUEST_MODE_EDIT
              ? this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls['requestedAmount'].value
                  .replace('€', '')
                  .trim()
              : this.requestedAmount.replace('€', '').trim(),
          deadline: '',
          sector: this._selectedProductLine,
          paymentMethod: this.requestDataUptakeForm.controls.formPayment.value,
          amount:
            this.mode === REQUEST_MODE_EDIT
              ? this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].controls['priceProduct'].value
                  .replace('€', '')
                  .trim()
              : this.priceProduct.replace('€', '').trim(),
          codeTariff: this.requestDataUptakeForm.controls.tariffs.value,
          deposit: this.start,
          dayDeadline: this.requestDataUptakeForm.controls.expirationDay.value,
          installments: this.requestDataUptakeForm.controls.term.value,
          vendor: typeof this.vendor === 'string' ? this.vendor : this.vendor.id,
          captacionCanal: '',
        };
        this.sendDataFinance.emit({ request: dataCallSimulation, export: false, click: clickButton });
      } else {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.width = '90%';
        dialogConfig.maxWidth = '550px';
        dialogConfig.data = {
          title: this.translateService.instant('dialog.title.installments'),
          text: this.translateService.instant('dialog.text.installments'),
          icon: 'icon-ic_warning_red',
        };
        this.dialog.open(ResponseDialogComponent, dialogConfig);
      }
    } else {
      this.requestStore.listaFormulariosConsumo[FINANCE_AMOUNT].markAllAsTouched();
      this.requestStore.listaFormulariosConsumo[REQUEST_DATA].markAllAsTouched();
      this.requestStore.listaFormulariosConsumo[POINT_OF_SALE].markAllAsTouched();
    }
  }

  updateCombos(form: FormGroup) {
    if (form) {
      Object.keys(this.requestDataUptakeForm.controls).forEach((key) => {
        if (form.controls[key] !== null) {
          this.requestDataUptakeForm.controls[key].setValue(form.controls[key].value);
        }
      });
      this.isCombosUpdateLoading = false;
    }
  }

  numberOnly(event: any, element: any): boolean {
    const inputForm = typeof element === 'string' ? element : element?.getAttribute('formControlName');
    let value = this.requestDataUptakeForm.controls[inputForm].value;

    return this.customValidators.numberOnly(event.which ? event.which : event.keyCode, value);
  }

  transformAmount(value: any, element: any) {
    const inputForm = typeof element === 'string' ? element : element?.getAttribute('formControlName');
    let inputValue = typeof value === 'string' ? value : value?.target.value;

    this.requestDataUptakeForm.controls[inputForm].setValue(this.customValidators.transformAmount(inputValue));
  }

  setValuesToForm() {
    this.requestDataUptakeForm.controls.expirationDay.setValue(this.request.expirationDay);
    this.requestDataUptakeForm.controls.expirationMonth.setValue(this.request.expirationDayName);
    this.requestDataUptakeForm.controls.commercialProduct.setValue(this.request.commercialProduct);
    this.requestDataUptakeForm.controls.term.setValue(this.request.numPlaz);
    this.requestDataUptakeForm.controls.tariffs.setValue(this.request.tariffSelected);
    this.requestDataUptakeForm.controls.formPayment.setValue(this.request.formPayment);
    this.requestDataUptakeForm.controls.reference.setValue(this.request.reference);
    this.requestDataUptakeForm.controls.insurance.setValue(this.request.descCodPac);
    this.requestDataUptakeForm.controls.share.setValue(this.request.feeAmount + ' €');
    this.vendor = this.request.vendor;
  }

  changeLoadingState() {
    if (
      !this.isComponentLoading &&
      !this.isExpirationDaysLoading &&
      !this.isCombosUpdateLoading &&
      !this.isChangesLoading &&
      !this.isTariffLoading &&
      !this.isModalityPaymentLoading
    ) {
      this.isFinishedLoading.emit({ formID: REQUEST_DATA, value: true });
      if (this.mode === REQUEST_MODE_EDIT) {
        this.calculateFinance();
      } else if (this.calculatorData && this.requestDataUptakeForm.valid) {
        setTimeout(() => {
          this.calculateFinance();
        }, 1500);
        this.calculatorData = false;
      }
    } else {
      this.isFinishedLoading.emit({ formID: REQUEST_DATA, value: false });
    }
  }

  private reset = () => {
    this.commercialProductList = [];
    this.tariffsList = [];
    this.formPaymentList = [];
    this.requestDataUptakeForm?.reset();
  };

  private disableFieldsByCore() {
    if (this.fieldsDisabled !== null && this.fieldsDisabled !== undefined) {
      const form = this.requestDataUptakeForm;
      this.fieldsDisabled.forEach((field) => {
        const toDisable = form.get(field.campoName);
        if (toDisable !== null && toDisable !== undefined) {
          toDisable.disable();
        }
      });
    }
  }
}
